<script>
    import { page } from '$app/stores';
    import Card from '$lib/Card.svelte';
    import CourseBanner from '$lib/CourseBanner.svelte';
    import WorksheetBanner from '$lib/WorksheetBanner.svelte';
    import Bundle from '$lib/Bundle.svelte';
    import Form from '$lib/Form.svelte';
    import Breadcrumbs from '$lib/Breadcrumbs.svelte';
    import KoFiDonate from '$lib/KoFiDonate.svelte';
    import PageLayout from '$lib/PageLayout.svelte';
    import PageSection from '$lib/PageSection.svelte';
    import Prose from '$lib/Prose.svelte';
    import ProductsList from '$lib/ProductsList.svelte';
    import ArticleSchema from '$lib/components/ArticleSchema.svelte';
    import SinglePageGame from '$lib/SinglePageGame.svelte';
    import Button from '$lib/Button.svelte';
    import Icon from '$lib/Icon.svelte';
    import { afterUpdate, onDestroy, tick } from 'svelte';

    export let data;

    /** @type {HTMLDivElement | null} */
    let content_wrapper = null;
    /** @type {Array<{ $destroy: () => void }>} */
    let mounted_product_cards = [];
    let enhanced_content = null;
    let enhanced_courses = null;
    let enhanced_bundles = null;
    let enhanced_worksheets = null;
    let enhancement_id = 0;

    let courses =
        data.show_courses !== false
            ? data?.courses?.filter((course) => course.price) || []
            : [];

    let worksheets =
        data.show_worksheets !== false ? data?.worksheets || [] : [];

    /** @type {Intl.DateTimeFormatOptions} */
    const date_options = {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    };

    const publish_text = data?.date
        ? `Published ${new Date(data.date).toLocaleDateString(
              'en-US',
              date_options
          )}`
        : '';

    const updated_text = data?.date_modified
        ? `Updated ${new Date(data.date_modified).toLocaleDateString(
              'en-US',
              date_options
          )}`
        : '';

    const inline_date_value =
        data?.hide_date !== true
            ? [publish_text, updated_text].filter(Boolean).join('; ')
            : '';

    const inline_date_style = inline_date_value
        ? `--inline-date:"${inline_date_value}";`
        : undefined;

    function destroy_product_cards() {
        for (const component of mounted_product_cards) {
            component.$destroy();
        }

        mounted_product_cards = [];
    }

    async function enhance_product_cards() {
        const current_enhancement_id = ++enhancement_id;
        await tick();

        if (current_enhancement_id !== enhancement_id || !content_wrapper) {
            return;
        }

        destroy_product_cards();

        const nodes = content_wrapper.querySelectorAll('.js-product-card');

        for (const node of nodes) {
            if (!(node instanceof HTMLElement)) continue;

            const product_type = node.dataset.productType || '';
            const slug = node.dataset.slug || '';
            const title_override = node.dataset.title || undefined;
            const lead_override = node.dataset.lead || undefined;
            const h_level_parsed = parseInt(node.dataset.hLevel || '', 10);
            const h_level = Number.isFinite(h_level_parsed)
                ? h_level_parsed
                : 2;

            if (product_type === 'course') {
                const course = data?.courses?.find((c) => c.slug === slug);

                if (!course) {
                    console.warn('content_product_card_missing_course', {
                        slug
                    });
                    continue;
                }

                node.innerHTML = '';
                mounted_product_cards.push(
                    new CourseBanner({
                        target: node,
                        props: {
                            course,
                            feature: `content-page`,
                            h_level,
                            title: title_override,
                            lead: lead_override
                        }
                    })
                );
            } else if (product_type === 'bundle') {
                const bundle = data?.bundles?.find((b) => b.slug === slug);

                if (!bundle) {
                    console.warn('content_product_card_missing_bundle', {
                        slug
                    });
                    continue;
                }

                node.innerHTML = '';
                mounted_product_cards.push(
                    new Bundle({
                        target: node,
                        props: {
                            bundle,
                            feature: `content-page`,
                            h_level,
                            title: title_override,
                            lead: lead_override
                        }
                    })
                );
            } else if (product_type === 'worksheet') {
                const worksheet = data?.worksheets?.find(
                    (w) => w.slug === slug
                );

                if (!worksheet) {
                    console.warn('content_product_card_missing_worksheet', {
                        slug
                    });
                    continue;
                }

                node.innerHTML = '';
                mounted_product_cards.push(
                    new WorksheetBanner({
                        target: node,
                        props: {
                            worksheet,
                            feature: `content-page`,
                            h_level,
                            title: title_override,
                            lead: lead_override
                        }
                    })
                );
            }
        }
    }

    afterUpdate(() => {
        if (data.type === 'game') return;
        if (
            enhanced_content === data.content &&
            enhanced_courses === data.courses &&
            enhanced_bundles === data.bundles &&
            enhanced_worksheets === data.worksheets
        ) {
            return;
        }

        enhanced_content = data.content;
        enhanced_courses = data.courses;
        enhanced_bundles = data.bundles;
        enhanced_worksheets = data.worksheets;
        enhance_product_cards();
    });

    onDestroy(() => {
        enhancement_id += 1;
        destroy_product_cards();
    });
</script>

{#if data.type === 'game'}
    <Button
        outline
        class="!fixed top-5 left-5 z-30 bg-black/20 backdrop-blur"
        route="/"><Icon arrow_left sm class="-mt-0.5" />Leave</Button
    >
    <SinglePageGame body_html={data.content} reward={data.reward} />
{:else}
    <PageLayout>
        <ArticleSchema
            title={data.head_title}
            description={data.head_description}
            author={data.author || 'Stephanie Simoes'}
            date_published={data.date}
            date_modified={data.date_modified}
            image={data.image}
            path={$page.url.pathname}
        />
        {#if Array.isArray(data.breadcrumbs) && data.breadcrumbs.length > 0}
            <Breadcrumbs breadcrumbs={data.breadcrumbs} />
        {/if}

        <PageSection
            class={`grid gap-8 ${data.hide_products ? '' : 'lg:grid-cols-[minmax(0,1fr)_auto]'} lg:auto-rows-min lg:items-start lg:gap-x-12 lg:gap-y-6`}
        >
            <div
                bind:this={content_wrapper}
                class="lg:mb-0 lg:col-start-1 lg:row-start-1"
            >
                <Prose
                    class={`${inline_date_value ? 'inline-date' : ''}`}
                    tag="article"
                    style={inline_date_style}
                >
                    {@html data.content}
                </Prose>

                <hr />
                {#if data.show_donate}
                    <Card class="mt-4 md:p-8 mx-auto max-w-2xl text-center">
                        <KoFiDonate />
                    </Card>
                {:else if data.show_subscribe !== false}
                    <Card class="mt-4 md:p-8 mx-auto max-w-2xl">
                        <Form
                            label_text="Don't miss new posts! Subscribe to my newsletter:"
                            cta_text="Subscribe"
                            success_message="Thank you for subscribing!"
                        />
                    </Card>
                {/if}
            </div>
            {#if !data.hide_products}
                <aside
                    class="w-full lg:max-w-sm space-y-8 lg:col-start-2 lg:row-span-2"
                >
                    <ProductsList
                        products={courses}
                        title="Interactive Courses"
                        title_href="/courses"
                    />

                    <ProductsList
                        products={worksheets}
                        title="Printable materials"
                        title_href="/worksheets"
                        limit={5}
                    />
                </aside>
            {/if}
        </PageSection>
    </PageLayout>
{/if}

<style>
    :global(.inline-date h1:first-of-type)::after {
        content: var(--inline-date, '');
        display: block;
        margin-top: 0.25rem;
        font-size: 0.75rem;
        line-height: 1rem;
        color: var(--symbols-muted);
        font-weight: 400;
    }
</style>
